import { dayjs } from "utils";
import { useEffect, useMemo, useReducer } from "react";
import { ospThemes } from "@ospweb/sdk-ui";
import pick from "lodash-es/pick";
import {
  PricePoint,
  Stock,
  StockMarketEnum,
  TradingData,
} from "../features/api/openData";
import { ISegment } from "../components/createChart";
import { convertTimeToGTM } from "../utils/stock";
import { useStockData } from "./useSubscribStock";
import { MaybeTradingData } from "./useStockDetailsMarket";
import { useIsPc } from "@/src/hooks/useIsPc";

const content_brand_primary = ospThemes.colors.content_brand_primary;
const white_20 = ospThemes.colors.border_inverse_transparent20;
const ONE_MINUTE = 60 * 1000;
const FIFTEEN_MINUTE = 15 * ONE_MINUTE;
const ONE_HOUR = 60 * ONE_MINUTE;
// 可误差范围
const DEVIATION = 20 * 1000;

const intervalMap: Record<string, number> = {
  "1d": ONE_MINUTE,
  "5d": FIFTEEN_MINUTE,
  "30d": ONE_HOUR,
};

const mockMarket = 0;

type IParams = {
  name?: string;
  logo?: string;
  dataType?: string;
  trading_data?: MaybeTradingData;
} & Pick<Stock, "sparkline" | "market">;

type SparklineData = {
  timestamp: number;
  price: number | null;
};

type Sparkline = PricePoint;

type IState = {
  sparkline: Array<
    Required<Pick<Sparkline, "timestamp">> & {
      price?: MaybeOrNull<number | string>;
    }
  >;
  dataType?: string;
  name?: string;
  market?: StockMarketEnum;
  tradingData: Pick<
    TradingData,
    | "regular_market_open_time"
    | "regular_market_close_time"
    | "previous_regular_market_open_time"
    | "previous_regular_market_close_time"
    | "pre_market_open_time"
    | "post_market_close_time"
  >;
};

const fillInterval = (params: {
  start: SparklineData;
  end: SparklineData;
  interval: number;
  now: number;
  exceptionTime?: { start: number; end: number };
}) => {
  const { start, end, interval, now, exceptionTime } = params;
  const newSparkLine: SparklineData[] = [];
  let current = start.timestamp;
  while (current < end.timestamp) {
    if (
      exceptionTime &&
      current > exceptionTime.start &&
      current < exceptionTime.end
    ) {
      // 跳过异常时间
      current += interval;
      continue;
    }
    newSparkLine.push({
      timestamp: current,
      price: current > now ? null : start.price,
      // label: dayjs(current).format("YYYY-MM-DD HH:mm"),
    });
    current += interval;
  }
  return newSparkLine;
};

// 获取不补点的例外时间
const getExceptionTime = (
  originSchedule: any,
): Maybe<{ start: number; end: number }> => {
  const REGULAR_MARKET =
    originSchedule?.schedule?.current?.phases?.REGULAR_MARKET;
  if (Array.isArray(REGULAR_MARKET) && REGULAR_MARKET.length > 1) {
    const start = Math.min(...REGULAR_MARKET.map((item) => item.end));
    const end = Math.max(...REGULAR_MARKET.map((item) => item.start));
    return {
      start,
      end,
    };
  }
};

// 获取待补充的数据 接产品信息 中间不补点
const getMissingData = (params: {
  sparkline: SparklineData[];
  begin?: SparklineData;
  dataType: string;
  now: number;
  endTimestamp: number;
  exceptionTime?: Maybe<{ start: number; end: number }>;
}) => {
  const { sparkline, dataType, now, endTimestamp, exceptionTime, begin } =
    params;

  // log.debug("[useStockLineChart] getMissingData sparkline", {sparkline, endTimestamp, begin,exceptionTime});
  // 如果sparkline为空，且begin为空，则返回原始sparkline；因为不知道如何填充
  if (!sparkline.length && !begin) {
    return sparkline;
  }
  const newSparkLine: SparklineData[] = [];
  if (begin && !sparkline.length) {
    newSparkLine.push(
      ...fillInterval({
        start: begin,
        end: {
          timestamp: endTimestamp + intervalMap[dataType],
          price: begin?.price,
        },
        interval: intervalMap[dataType],
        now,
        exceptionTime,
      }),
    );
    return newSparkLine;
  }
  if (begin && begin.timestamp < sparkline[0].timestamp) {
    newSparkLine.push(
      ...fillInterval({
        start: begin,
        end: sparkline[0],
        interval: intervalMap[dataType],
        now,
        exceptionTime,
      }),
    );
  }
  let slow = 0,
    fast = 1;
  while (sparkline[slow] && sparkline[fast]) {
    if (
      sparkline[fast].timestamp - sparkline[slow].timestamp >
      intervalMap[dataType]
    ) {
      newSparkLine.push(
        ...fillInterval({
          start: sparkline[slow],
          end: sparkline[fast],
          interval: intervalMap[dataType],
          now,
          exceptionTime,
        }),
      );
    } else {
      newSparkLine.push(sparkline[slow]);
    }
    slow++;
    fast++;
  }
  // log.debug("[useStockLineChart] getMissingData newSparkLine", newSparkLine);
  const start = sparkline?.[slow];
  newSparkLine.push(
    ...fillInterval({
      start,
      end: {
        timestamp: endTimestamp + intervalMap[dataType],
        price: start?.price,
      },
      now,
      interval: intervalMap[dataType],
      exceptionTime,
    }),
  );
  return newSparkLine;
};

// 将socket数据添加到折线内，并返回应该添加的数据
// 这里主要考虑的是latestData 和 socketData 隔了比较长时间的情况
const addSocketData = (params: {
  latestData: IState["sparkline"][number];
  socketData: { t: number; p: string; s: string };
  dataType: string;
}) => {
  const { latestData, socketData, dataType } = params;
  const lastTimestamp = latestData?.timestamp ?? Date.now();
  let nextTimestamp = lastTimestamp + intervalMap[dataType];
  const addSparkline = [];
  while (nextTimestamp <= socketData.t) {
    if (Math.abs(socketData.t - nextTimestamp) < DEVIATION) {
      addSparkline.push({
        timestamp: nextTimestamp,
        price: socketData.p,
      });
      nextTimestamp += intervalMap[dataType];
    } else {
      // 与产品沟通，不补点 中间缺少就缺少
      // addSparkline.push({
      //   timestamp: nextTimestamp,
      //   price: latestData?.price || null,
      // });
      nextTimestamp += intervalMap[dataType];
    }
  }
  return addSparkline;
};
// 添加socket数据到折线内，并返回修改后的数据
// 这里主要考虑的是盘中的时候X轴已经全部补全了
// 和产品沟通，不补点 中间缺少就缺少 所以这个往前追溯的逻辑注释掉
// const addSocketDataToSparkline = (params: {
//   sparkline: IState["sparkline"];
//   socketData: SparklineData;
//   index: number;
// }) => {
//   const { sparkline, socketData, index } = params;
//   sparkline[index].price = socketData.price;
//   let preIndex = index - 1;
//   while (
//     preIndex > 0 &&
//     (sparkline[preIndex].price === null ||
//       sparkline[preIndex].price === undefined)
//   ) {
//     preIndex--;
//   }
//   if (index - preIndex <= 1) {
//     return [...sparkline];
//   }
//   const prevPrice = sparkline[preIndex].price;
//   preIndex = preIndex + 1;
//   while (preIndex < index) {
//     sparkline[preIndex].price = prevPrice;
//     preIndex++;
//   }
//   return [...sparkline];
// };

const reducer = (state: IState, action: { type: string; payload: any }) => {
  const now = Date.now() + mockMarket;
  switch (action.type) {
    case "INITIALIZE": {
      const {
        sparkline,
        name,
        dataType,
        market,
        tradingData: {
          regular_market_open_time,
          regular_market_close_time,
          previous_regular_market_open_time,
          previous_regular_market_close_time,
          pre_market_open_time,
          post_market_close_time,
          originSchedule,
          open,
          current_price,
        },
      } = action.payload;
      let newSparkline =
        sparkline?.filter((item: Sparkline) => !!item.timestamp) || [];
      if (dataType === "1d") {
        const exceptionTime = getExceptionTime(originSchedule);
        const begin = {
          timestamp: regular_market_open_time,
          price: !!sparkline?.length ? open : current_price,
        };
        // log.debug("[useStockLineChart] INITIALIZE begin", originSchedule, exceptionTime);
        // 补点逻辑 dataType === "1d" 时 进行补点
        newSparkline = getMissingData({
          sparkline: newSparkline,
          dataType,
          now,
          begin,
          endTimestamp:
            market === StockMarketEnum.US
              ? post_market_close_time
              : regular_market_close_time,
          exceptionTime,
        });
      }
      return {
        ...state,
        dataType,
        name,
        sparkline: [...newSparkline],
        tradingData: {
          regular_market_open_time,
          regular_market_close_time,
          previous_regular_market_open_time,
          previous_regular_market_close_time,
          pre_market_open_time,
          post_market_close_time,
        },
      };
    }
    case "UPDATE_DATA": {
      const {
        sparkline,
        dataType,
        name,
        tradingData: {
          regular_market_open_time,
          regular_market_close_time,
          pre_market_open_time,
          post_market_close_time,
        },
      } = state;
      if (
        !pre_market_open_time ||
        !post_market_close_time ||
        !dataType ||
        !regular_market_open_time ||
        !regular_market_close_time
      ) {
        return state;
      }
      if (now < pre_market_open_time || now >= post_market_close_time) {
        return state;
      }
      let { symbolData } = action.payload;
      if (!symbolData?.t || symbolData?.s !== name) {
        return state;
      }
      symbolData = {
        t: dayjs(symbolData.t).startOf("minute").valueOf(),
        p: symbolData.p,
        s: symbolData.s,
      };
      if (
        now >= regular_market_open_time &&
        now < post_market_close_time &&
        dataType === "1d"
      ) {
        if (!sparkline.length) {
          return {
            ...state,
            sparkline: [
              {
                timestamp: symbolData.t,
                price: symbolData.p,
              },
            ],
          };
        }
        // 说明是盘中或盘后阶段
        const prevIndex = sparkline.findIndex(
          (item) => Math.abs(symbolData.t - item.timestamp) < DEVIATION,
        );
        if (prevIndex < 0) {
          return state;
        }
        // 与产品沟通，不补点 所以不往前追溯 中间缺少就缺少
        // const list = addSocketDataToSparkline({
        //   sparkline,
        //   socketData: symbolData,
        //   index: prevIndex,
        // });
        sparkline[prevIndex].price = symbolData.p;
        return {
          ...state,
          sparkline: [...sparkline],
        };
      }
      // 说明是盘前或者5D或者30D
      let lastItem = sparkline.at(-1);
      if (!lastItem) {
        return {
          ...state,
          sparkline: [
            {
              timestamp: symbolData.t,
              price: symbolData.p,
            },
          ],
        };
      }
      // 如果接口返回的最后一个时间点小于盘前交易开始时间，说明最后一个点是前一个交易日的，同时又缺少今天的第一个点
      if (lastItem.timestamp < pre_market_open_time) {
        lastItem = {
          timestamp: pre_market_open_time,
          price: null,
        };
      }
      const addSparkline = addSocketData({
        latestData: lastItem,
        socketData: symbolData,
        dataType,
      });
      if (!addSparkline.length) {
        return state;
      }
      const newData = [...sparkline, ...addSparkline];
      return {
        ...state,
        sparkline: [...newData],
      };
    }
    default:
      return state;
  }
};

// const useTestSocket = (name: string) => {
//   const count = useRef(0);
//   const start = useRef(dayjs().startOf("minute").valueOf());
//   const [state, setState] = useState<{ t: number; p: string; s: string }>();
//   useEffect(() => {
//     setInterval(() => {
//       count.current++;
//       const time = start.current + (count.current * intervalMap["1d"]);
//       setState({
//         t: time,
//         p: (Math.random() * 550).toFixed(2),
//         s: name,
//       });
//     }, 5000)
//   }, [name])
//   return {
//     data: state,
//   }
// }

export const useStockLineChart = (params: IParams) => {
  const {
    name, // 股票名称
    logo, // 股票logo
    sparkline,
    dataType,
    market,
    trading_data,
  } = params;
  const { data: symbolData } = useStockData(name || "");
  const [isPC] = useIsPc();

  const [state, dispatch] = useReducer(reducer, {
    sparkline: params.sparkline as IState["sparkline"],
    dataType: params.dataType,
    name: params.name,
    market,
    tradingData: pick(trading_data || {}, [
      "regular_market_open_time",
      "regular_market_close_time",
      "previous_regular_market_open_time",
      "previous_regular_market_close_time",
      "pre_market_open_time",
      "post_market_close_time",
      "originSchedule",
      "open",
      "current_price",
    ]),
  });

  useEffect(() => {
    if (!dataType) {
      return;
    }
    dispatch({
      type: "INITIALIZE",
      payload: {
        sparkline,
        name,
        dataType,
        market,
        tradingData: pick(trading_data || {}, [
          "regular_market_open_time",
          "regular_market_close_time",
          "previous_regular_market_open_time",
          "previous_regular_market_close_time",
          "pre_market_open_time",
          "post_market_close_time",
          "originSchedule",
          "open",
          "current_price",
        ]),
      },
    });
  }, [name, dataType, sparkline, market, trading_data]);

  useEffect(() => {
    if (!symbolData) {
      return;
    }
    dispatch({
      type: "UPDATE_DATA",
      payload: { symbolData },
    });
  }, [symbolData]);

  const xAxisData = useMemo(() => {
    return state.sparkline?.map((item) => {
      return {
        x: item.timestamp,
        label: convertTimeToGTM(item.timestamp, market, "MMM DD HH:mm"),
      };
    });
  }, [state.sparkline, market]);

  const yAxisData = useMemo(() => {
    if (!state.name || !state.sparkline || !state.dataType) {
      return { left: [] };
    }
    if (state.dataType !== "1d") {
      return {
        left: [
          {
            color: content_brand_primary,
            shadow: true,
            name: state.name,
            data: state.sparkline.map((item) =>
              item.price ? Number(Number(item.price).toFixed(2)) : null,
            ),
            logo,
          },
        ],
      };
    }
    const data = state.sparkline.reduce<Array<ISegment>>((acc, curr) => {
      const lastItem = acc.at(-1);
      const price = curr?.price ? Number(Number(curr?.price).toFixed(2)) : null;
      if (!curr.timestamp) {
        return acc;
      }
      // 是否是在开盘时间范围内
      if (
        (state.tradingData.regular_market_open_time &&
          state.tradingData.regular_market_close_time &&
          curr.timestamp >= state.tradingData.regular_market_open_time &&
          curr.timestamp < state.tradingData.regular_market_close_time) ||
        (state.tradingData.previous_regular_market_open_time &&
          state.tradingData.previous_regular_market_close_time &&
          curr.timestamp >=
            state.tradingData.previous_regular_market_open_time &&
          curr.timestamp < state.tradingData.previous_regular_market_close_time)
      ) {
        if (lastItem && lastItem.color === content_brand_primary) {
          lastItem.data.push(price);
          return acc;
        }
        acc.push({
          color: content_brand_primary,
          shadow: true,
          data: [price],
        });
        return acc;
      }
      if (lastItem && lastItem.color === white_20) {
        lastItem.data.push(price);
        return acc;
      }
      acc.push({
        color: white_20,
        shadow: true,
        data: [price],
      });
      return acc;
    }, []);
    return {
      left: [
        {
          // 折线颜色
          color: content_brand_primary,
          // 是否显示阴影
          shadow: true,
          // 数据名称
          name: state.name,
          // 数据
          data,
          logo,
        },
      ],
    };
  }, [logo, state]);

  const xKeyTicks = useMemo(() => {
    if (!state.sparkline?.length) {
      return [];
    }
    const zone =
      market === StockMarketEnum.US ? "America/New_York" : "Asia/Hong_Kong";
    if (state.sparkline.length < 5) {
      if (isPC) {
        return state.sparkline.map((item) => {
          return {
            x: item.timestamp,
            label: dayjs(item.timestamp).tz(zone).format("MMM DD, HH:mm"),
          };
        });
      }
      const start = state.sparkline[0];
      const end = state.sparkline[2] || state.sparkline[1];

      return [start, end].map((item) => {
        return {
          x: item.timestamp,
          label: dayjs(item.timestamp).tz(zone).format("MMM DD, HH:mm"),
        };
      });
    }
    const count = isPC ? 5 : 3;
    const interval = Math.floor(state.sparkline.length / count);
    const ticks = Array.from({ length: count - 1 }).map((_, i) => {
      const item = state.sparkline[interval * (i + 1)];
      return {
        x: item.timestamp,
        label: dayjs(item.timestamp).tz(zone).format("MMM DD, HH:mm"),
      };
    });
    return ticks;
  }, [state.sparkline, isPC, market]);
  return {
    xAxisData,
    yAxisData,
    xKeyTicks,
  };
};
